import { RiskBadge } from '@/components/RiskBadge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { WidgetTable } from '@/components/WidgetTable';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { buildGroupedAlertDeepLinkUrl } from '@/pages/organization/tabs/DataTableAlertsTab/utils';
import { useQuery } from '@tanstack/react-query';
import { listOrganizationGroupedAlerts } from '@kirin/api-client/api/alerts';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { OrganizationScanAlertsSortField } from '@kirin/api-client/contracts/alerts';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { apiClient } from '@/lib/api-client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  formatSubPolicyDescription,
  formatSubPolicyLabel,
} from '@kirin/api-client/helpers/alerts';
import { getSeverityLabel } from '@kirin/api-client/helpers/severity';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';

const LIMIT = 10;

/**
 * Wraps (breaking long words if it must, so Last Seen keeps its room), but
 * never past two lines; the `title` tooltip has the rest.
 */
const TWO_LINES = 'line-clamp-2 whitespace-normal wrap-anywhere';

export function LatestAlertsWidget(): React.ReactNode {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.ALERTS_REVAMP]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;

  const handleOnClickAlert = (alert: GroupedAlert) => {
    const url = buildGroupedAlertDeepLinkUrl(organizationId, alert);
    if (url) {
      navigate(url);
    }
  };

  const { isLoading, data, isError, refetch } = useQuery({
    queryKey: ['alerts', 'organization', organizationId ?? '', 'latest'],
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listOrganizationGroupedAlerts(apiClient, organizationId, {
        page: 1,
        limit: LIMIT,
        sortBy: OrganizationScanAlertsSortField.LastSeenAt,
        sortOrder: SortOrder.Desc,
      });
    },
    enabled: !!organizationId,
  });

  const alerts: GroupedAlert[] =
    (data && 'data' in data ? data.data : data?.alerts) ?? [];

  return (
    <DashboardWidgetWrapper
      isLoading={isLoading}
      isError={isError}
      className="xl:col-span-6"
      cardClassName="xl:h-full"
      title="Latest Alerts"
      description="Shows up to 10 Alerts"
      refetch={refetch}
    >
      <WidgetTable<GroupedAlert>
        data={alerts}
        className="flex-1 min-h-0 @container"
        columns={[
          {
            id: 'type',
            header: revampEnabled ? 'Title' : 'Type',
            cell: a => {
              const label = revampEnabled
                ? formatSubPolicyLabel(a.subPolicyKey, a.type)
                : a.type;
              // min-w-20 keeps ordinary words whole; only very long ones break
              return (
                <span className={`${TWO_LINES} min-w-20`} title={label}>
                  {label}
                </span>
              );
            },
            // Title + Description take 85%, not 100%: the rest is Severity's
            // and Last Seen's, so no engine has to shrink an over-constrained
            // table to fit them.
            className: 'w-[35%] font-medium',
          },
          {
            id: 'severity',
            header: 'Severity',
            // Icon only while the table is narrow, so Title keeps its room;
            // the label stays for screen readers, and in a tooltip on hover
            // (which, unlike `title`, isn't announced a second time).
            cell: a => (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <RiskBadge
                      risk={a.severity}
                      showIcon
                      labelClassName="sr-only @lg:not-sr-only"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{getSeverityLabel(a.severity)}</TooltipContent>
              </Tooltip>
            ),
            className: 'w-px',
          },
          {
            id: 'description',
            header: 'Description',
            cell: a => {
              const description = revampEnabled
                ? formatSubPolicyDescription(
                    a.subPolicyKey,
                    a.type,
                    a.description
                  )
                : a.description;
              return (
                <span className={TWO_LINES} title={description}>
                  {description}
                </span>
              );
            },
            className: 'w-[50%]',
          },
          {
            id: 'lastSeen',
            header: 'Last Seen',
            // The relative form ("about 3 hours ago") stays on one line, so
            // it sets the column's width; the absolute form still breaks only
            // between its date and time.
            cell: a => (
              <span className="block min-w-[5.5rem] text-muted-foreground font-medium tabular-nums [&_span]:whitespace-nowrap">
                <RelativeTimeWithTooltip date={a.lastSeenAt} />
              </span>
            ),
            className: 'w-px',
          },
        ]}
        emptyState="No Alerts"
        onRowClick={handleOnClickAlert}
        getRowId={row => row.alertKey}
      />
    </DashboardWidgetWrapper>
  );
}
